import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { AttachMoney, FileCopy, CheckCircle } from "@material-ui/icons";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: "400px",
  },
  content: {
    padding: theme.spacing(3),
  },
  amountField: {
    marginBottom: theme.spacing(2),
  },
  descriptionField: {
    marginBottom: theme.spacing(2),
  },
  linkBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  linkText: {
    wordBreak: "break-all",
    color: theme.palette.primary.main,
    fontSize: "14px",
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
  successIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
}));

const StripePaymentModal = ({ open, onClose, ticketId, contactName }) => {
  const classes = useStyles();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Pago");
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Por favor ingrese un monto válido");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/stripe/payment-link", {
        amount: parseFloat(amount),
        description: description || "Pago",
        ticketId,
        contactName,
      });

      setPaymentLink(data);
      toast.success("Link de pago generado exitosamente");
    } catch (error) {
      console.error("Error generating payment link:", error);
      toast.error(
        error.response?.data?.error || "Error al generar link de pago"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (paymentLink?.url) {
      navigator.clipboard.writeText(paymentLink.url);
      setCopied(true);
      toast.success("Link copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendLink = () => {
    if (paymentLink?.url) {
      // Copiar el link y cerrar el modal para que el usuario lo pegue en el chat
      navigator.clipboard.writeText(paymentLink.url);
      toast.success("Link copiado. Pégalo en el chat para enviarlo");
      handleClose();
    }
  };

  const handleClose = () => {
    setAmount("");
    setDescription("Pago");
    setPaymentLink(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>Generar Link de Pago Stripe</DialogTitle>
      <DialogContent className={classes.content}>
        {!paymentLink ? (
          <>
            <TextField
              label="Monto (USD)"
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={classes.amountField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
                ),
              }}
              placeholder="0.00"
              inputProps={{
                min: "0",
                step: "0.01",
              }}
              disabled={loading}
            />
            <TextField
              label="Descripción"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={classes.descriptionField}
              placeholder="Descripción del pago"
              disabled={loading}
            />
            {contactName && (
              <Typography variant="body2" color="textSecondary">
                Cliente: {contactName}
              </Typography>
            )}
          </>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <CheckCircle className={classes.successIcon} />
              <Typography variant="h6">Link generado exitosamente</Typography>
            </Box>
            <Typography variant="body2" gutterBottom>
              <strong>Monto:</strong> ${paymentLink.amount} {paymentLink.currency}
            </Typography>
            <Box className={classes.linkBox}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Link de pago:
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography className={classes.linkText}>
                  {paymentLink.url}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCopyLink}
                  className={classes.copyButton}
                  title="Copiar link"
                >
                  {copied ? (
                    <CheckCircle style={{ color: "green" }} />
                  ) : (
                    <FileCopy />
                  )}
                </IconButton>
              </Box>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Comparte este link con tu cliente para que realice el pago
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          {paymentLink ? "Cerrar" : "Cancelar"}
        </Button>
        {!paymentLink ? (
          <Button
            onClick={handleGenerateLink}
            color="primary"
            variant="contained"
            disabled={loading || !amount || parseFloat(amount) <= 0}
          >
            {loading ? <CircularProgress size={24} /> : "Generar Link"}
          </Button>
        ) : (
          <Button
            onClick={handleSendLink}
            color="primary"
            variant="contained"
          >
            Copiar y Enviar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StripePaymentModal;
